(() => {
  if (window.neuloCountryUpdateReady) return;
  window.neuloCountryUpdateReady = true;
  let pending = false;
  let manualChoiceMade = false;
  let detectionController;
  const sizeSystem = (country) => {
    if (['US', 'CA'].includes(country)) return 'us';
    if (['GB', 'AU'].includes(country)) return 'uk';
    if (['JP', 'TW', 'KR'].includes(country)) return 'jp';
    return 'eu';
  };
  document.addEventListener('change', async (event) => {
    const select = event.target;
    if (!select.matches('[data-neulo-country-select]') || pending) return;
    manualChoiceMade = true;
    if (detectionController) detectionController.abort();
    const form = select.closest('form');
    const product = document.querySelector('[data-multi-size-purchase][data-english="true"]');
    if (!product || !product.querySelector('[data-submit]')) { form.requestSubmit(); return; }
    if (product.dataset.cartPending === 'true') { select.value = product.dataset.country; return; }
    const country = select.value;
    if (country === product.dataset.country && product.dataset.countryPending !== 'true') return;
    pending = true;
    product.dataset.countryPending = 'true';
    product.setAttribute('aria-busy', 'true');
    const selectors = Array.from(document.querySelectorAll('[data-neulo-country-select]'));
    selectors.forEach((item) => {
      item.value = country; item.disabled = true;
      if (item !== select) item.dispatchEvent(new Event('change', {bubbles: true}));
    });
    const status = form.closest('.neulo-country-selector').querySelector('[data-neulo-country-status]');
    document.querySelectorAll('[data-neulo-country-status]').forEach((item) => { item.textContent = 'Updating prices and shipping…'; });
    document.querySelectorAll('[data-neulo-country-message]').forEach((item) => { item.hidden = true; });
    product.querySelector('[data-submit]').disabled = true;
    product.querySelectorAll('[data-neulo-country-fragment]').forEach((fragment) => {
      fragment.style.visibility = 'hidden';
      fragment.inert = true;
    });
    product.dispatchEvent(new CustomEvent('neulo:country-preview', {detail: {country, sizeSystem: sizeSystem(country)}}));
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      // Keep the Shopify country cookie and cart currency authoritative.
      // Disabled selects are omitted from FormData; add the requested country explicitly.
      const data = new URLSearchParams(new FormData(form));
      data.set('country_code', country);
      const response = await fetch(form.action, {method: 'POST', body: data, credentials: 'same-origin', signal: controller.signal});
      if (!response.ok || new URL(response.url).origin !== location.origin) throw new Error('country-response');
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = Array.from(page.querySelectorAll('[data-multi-size-purchase]')).find((item) => item.dataset.productId === product.dataset.productId);
      if (!fresh || fresh.dataset.country !== country) throw new Error('country-mismatch');
      // Validate every replacement before touching any displayed prices.
      const replacements = Array.from(product.querySelectorAll('[data-neulo-country-fragment]')).map((target) => {
        const source = fresh.querySelector(`[data-neulo-country-fragment="${target.dataset.neuloCountryFragment}"]`);
        if (!source) throw new Error('missing-fragment');
        return {target, source};
      });
      const newButtons = new Map(Array.from(fresh.querySelectorAll('[data-size-button]')).map((button) => [button.dataset.variantId, button]));
      replacements.forEach(({target, source}) => {
        target.innerHTML = source.innerHTML;
        target.style.visibility = '';
        target.inert = false;
      });
      product.querySelectorAll('[data-size-button]').forEach((button) => {
        const freshButton = newButtons.get(button.dataset.variantId);
        button.disabled = !freshButton || freshButton.disabled;
        if (freshButton) button.dataset.variantPrice = freshButton.dataset.variantPrice;
        const oldRestock = button.querySelector('.multi-size-purchase__restock');
        const freshRestock = freshButton?.querySelector('.multi-size-purchase__restock');
        if (oldRestock) oldRestock.remove();
        if (freshRestock) button.append(freshRestock.cloneNode(true));
      });
      product.dataset.country = fresh.dataset.country;
      product.dataset.international = fresh.dataset.international;
      product.dataset.sizeSystem = fresh.dataset.sizeSystem;
      product.dataset.cartAddUrl = fresh.dataset.cartAddUrl;
      product.dataset.cartUrl = fresh.dataset.cartUrl;
      document.querySelectorAll('.neulo-country-selector').forEach((selector) => {
        const message = selector.querySelector('[data-neulo-country-message]');
        const freshMessage = page.querySelector('[data-neulo-country-message]');
        if (message && freshMessage) { message.innerHTML = freshMessage.innerHTML; message.hidden = false; }
      });
      delete product.dataset.countryPending;
      product.removeAttribute('aria-busy');
      product.dispatchEvent(new CustomEvent('neulo:country-updated'));
      document.querySelectorAll('[data-neulo-country-status]').forEach((item) => { item.textContent = 'Prices and shipping updated.'; });
    } catch (_) {
      // A failed response may still have changed the Shopify country cookie.
      // Keep purchases blocked until a successful retry or a native form reload.
      document.querySelectorAll('[data-neulo-country-status]').forEach((item) => { item.textContent = 'Unable to confirm prices. Reload to try again.'; });
      const reload = document.createElement('button');
      reload.type = 'button';
      reload.textContent = 'Reload prices';
      reload.addEventListener('click', () => form.requestSubmit());
      status.append(' ', reload);
    } finally {
      clearTimeout(timeout);
      pending = false;
      selectors.forEach((item) => { item.disabled = false; });
    }
  });

  const detectInitialCountry = async () => {
    const select = document.querySelector('[data-neulo-country-select]');
    const product = document.querySelector('[data-multi-size-purchase][data-english="true"]');
    // Shopify handles the initial GeoIP context. Its saved localization wins on return visits.
    if (!select || !product || !product.querySelector('[data-submit]') || window.Shopify?.designMode) return;
    // This product entry starts in SGD, including visits with another country saved.
    // Reuse the normal change flow so prices, shipping and Shopify's cart agree.
    if (location.pathname.replace(/\/$/, '') === '/en/products/pathone') {
      if (Array.from(select.options).some((option) => option.value === 'SG') && product.dataset.country !== 'SG') {
        select.value = 'SG';
        select.dispatchEvent(new Event('change', {bubbles: true}));
      }
      return;
    }
    if (document.cookie.split(';').some((cookie) => cookie.trim().startsWith('localization='))) return;
    if (new URL(location.href).searchParams.has('country')) return;
    const endpoint = select.dataset.neuloCountryDetectionUrl;
    if (!endpoint) return;
    detectionController = new AbortController();
    const timeout = setTimeout(() => detectionController.abort(), 3000);
    try {
      const url = new URL(endpoint, location.origin);
      url.searchParams.set('country[enabled]', 'true');
      url.searchParams.set('language[enabled]', 'false');
      const response = await fetch(url, {credentials: 'same-origin', signal: detectionController.signal});
      if (!response.ok) return;
      const context = await response.json();
      const detected = context.detected_values?.country?.handle;
      const recommendation = context.suggestions?.find((item) => item.parts?.country?.handle === detected);
      const country = recommendation?.parts?.country;
      if (manualChoiceMade || pending || product.dataset.cartPending === 'true') return;
      if (!country || typeof country.confidence !== 'number' || country.confidence < 0.9 || !Array.from(select.options).some((option) => option.value === country.handle)) return;
      if (country.handle !== product.dataset.country) {
        select.value = country.handle;
        select.dispatchEvent(new Event('change', {bubbles: true}));
      }
    } catch (_) {
      // Keep Shopify's existing context if detection is unavailable; manual selection remains usable.
    } finally {
      clearTimeout(timeout);
      detectionController = null;
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', detectInitialCountry, {once: true});
  else detectInitialCountry();
})();
